import { useState } from 'react';
import axios from 'axios';
import { CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubmitIdea = () => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    full_name: '',
    contact: '',
    title: '',
    description: '',
    category: '',
    attachment_link: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Connect to backend API
      await axios.post(`${API}/ideas`, formData);
      
      setSubmitted(true);
      setSubmitStatus({
        type: 'success',
        message: 'Thanks for sharing your idea! Our team will review it and get in touch soon.'
      });
      setFormData({
        full_name: '',
        contact: '',
        title: '',
        description: '',
        category: '',
        attachment_link: ''
      });
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Failed to submit idea. Please try again.'
      });
      
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const categories = ['Tech', 'Business', 'Research', 'Science', 'Social', 'Other'];

  return (
    <motion.div 
      className={`pt-20 min-h-screen transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-[#0f0520] via-[#1a0b2e] to-[#2d1b4e]' 
          : 'bg-gradient-to-br from-white via-gray-50 to-purple-50'
      }`}
      data-testid="submit-idea-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className={`py-20 transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-[#1a0b2e] via-[#2d1b4e] to-[#1a0b2e]' 
          : 'bg-gradient-to-br from-indigo-600 to-purple-700'
      }`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <motion.div
            className="inline-flex items-center gap-2 px-6 py-2 rounded-full text-sm font-semibold mb-8 bg-yellow-500/20 text-yellow-300 border border-yellow-500/30"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            Share Your Vision
          </motion.div>

          {/* Lightbulb Icon */}
          <motion.div
            className="w-24 h-24 mx-auto mb-8 rounded-3xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-2xl"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: "spring" }}
          >
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </motion.div>

          {/* Heading */}
          <motion.h1 
            className="text-3xl md:text-4xl font-bold mb-4 text-white"
            data-testid="submit-idea-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Got a real-world problem or an exciting idea?
          </motion.h1>

          {/* Subheading */}
          <motion.p 
            className="text-lg text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Whether it's a startup idea, a tech innovation, or even a science or research-based project — 
            this is your chance to make it real.
          </motion.p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence>
            {submitted && submitStatus.type === 'success' && (
              <motion.div 
                className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 mb-8 shadow-lg" 
                data-testid="success-message"
                initial={{ opacity: 0, scale: 0.9, y: -20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex items-center mb-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                  >
                    <CheckCircle2 className="text-green-600 mr-3" size={24} />
                  </motion.div>
                  <h2 className="text-2xl font-bold text-green-800">Idea Submitted Successfully!</h2>
                </div>
                <p className="text-green-700 mb-6">{submitStatus.message}</p>
                <p className="text-green-700 mb-4">Meanwhile, join our community to stay updated.</p>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/community"
                    className="inline-block bg-green-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-700 transition shadow-lg hover:shadow-xl"
                    data-testid="success-join-community-link"
                  >
                    Join Our Community →
                  </Link>
                </motion.div>
              </motion.div>
            )}

            {submitStatus.type === 'error' && (
              <motion.div 
                className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-8 shadow-lg"
                initial={{ opacity: 0, scale: 0.9, y: -20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-red-700">{submitStatus.message}</p>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div 
            className={`rounded-3xl shadow-lg p-8 border ${
              isDark 
                ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' 
                : 'bg-white border-gray-200'
            }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.div
              className={`mb-6 pb-4 border-b-2 ${
                isDark ? 'border-purple-500/30' : 'border-indigo-200'
              }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <motion.h2 
                className={`text-2xl font-bold inline-block border-b-4 pb-2 ${
                  isDark 
                    ? 'text-white border-purple-500' 
                    : 'text-gray-900 border-indigo-600'
                }`}
              >
                Submit Your Idea
              </motion.h2>
            </motion.div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`} htmlFor="full_name">
                  Full Name *
                </label>
                <motion.input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border-2 rounded-xl transition shadow-sm hover:shadow-md ${
                    isDark 
                      ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                  data-testid="idea-full-name-input"
                  whileFocus={{ scale: 1.02 }}
                />
              </div>

              <div>
                <label className={`block text-sm font-semibold mb-2 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`} htmlFor="contact">
                  Contact (Email/Phone) *
                </label>
                <motion.input
                  type="text"
                  id="contact"
                  name="contact"
                  value={formData.contact}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border-2 rounded-xl transition shadow-sm hover:shadow-md ${
                    isDark 
                      ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                  data-testid="idea-contact-input"
                  whileFocus={{ scale: 1.02 }}
                />
              </div>

              <div>
                <label className={`block text-sm font-semibold mb-2 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`} htmlFor="title">
                  Title of Project *
                </label>
                <motion.input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border-2 rounded-xl transition shadow-sm hover:shadow-md ${
                    isDark 
                      ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                  data-testid="idea-title-input"
                  whileFocus={{ scale: 1.02 }}
                />
              </div>

              <div>
                <label className={`block text-sm font-semibold mb-2 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`} htmlFor="description">
                  Describe your problem / idea (what & why) *
                </label>
                <motion.textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={6}
                  required
                  className={`w-full px-4 py-3 border-2 rounded-xl transition shadow-sm hover:shadow-md resize-none ${
                    isDark 
                      ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                  data-testid="idea-description-input"
                  whileFocus={{ scale: 1.01 }}
                />
              </div>

              <div>
                <label className={`block text-sm font-semibold mb-2 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`} htmlFor="category">
                  Category *
                </label>
                <motion.select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border-2 rounded-xl transition shadow-sm hover:shadow-md ${
                    isDark 
                      ? 'bg-[#1a0b2e] border-purple-500/30 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                  data-testid="idea-category-select"
                  whileFocus={{ scale: 1.02 }}
                >
                  <option value="" className={isDark ? 'bg-[#1a0b2e]' : ''}>Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat} className={isDark ? 'bg-[#1a0b2e]' : ''}>
                      {cat}
                    </option>
                  ))}
                </motion.select>
              </div>

              <div>
                <label className={`block text-sm font-semibold mb-2 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`} htmlFor="attachment_link">
                  Attach link or doc (Optional)
                </label>
                <motion.input
                  type="url"
                  id="attachment_link"
                  name="attachment_link"
                  value={formData.attachment_link}
                  onChange={handleInputChange}
                  placeholder="https://..."
                  className={`w-full px-4 py-3 border-2 rounded-xl transition shadow-sm hover:shadow-md ${
                    isDark 
                      ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                  data-testid="idea-attachment-input"
                  whileFocus={{ scale: 1.02 }}
                />
                <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Link to research, article, prototype, or any supporting document
                </p>
              </div>

              <motion.button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-700 hover:to-pink-700 transition shadow-lg hover:shadow-xl"
                data-testid="submit-idea-button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Submit My Idea
              </motion.button>
            </form>
          </motion.div>

          {/* Tips Section */}
          <motion.div 
            className={`rounded-3xl shadow-lg p-8 border mt-8 ${
              isDark 
                ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' 
                : 'bg-white border-gray-200'
            }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex items-center gap-2 mb-6">
              <span className="text-2xl">💡</span>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Tips for a great submission
              </h3>
            </div>
            <ul className={`space-y-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <li className="flex items-start gap-3">
                <span className="text-purple-500 mt-1">•</span>
                <span>Clearly define the problem you're trying to solve</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-purple-500 mt-1">•</span>
                <span>Explain why this problem matters and who it affects</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-purple-500 mt-1">•</span>
                <span>Share any initial research or validation you've done</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-purple-500 mt-1">•</span>
                <span>Be specific about what you want to build</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default SubmitIdea;
